import {
  Checkbox,
  FormControlLabel,
  makeStyles,
  Theme,
} from "@material-ui/core"
import classNames from "classnames"
import React from "react"
import { lightBlack, lightestGray } from "../../../themes/variables"

interface Props {
  name?: string
  defaultChecked?: boolean
  value?: boolean
  required?: boolean
  onChange?: (value: boolean) => void
  onValueChange?: (value: boolean) => void
  className?: string
  customLabelClass?: string
  children?: React.ReactNode
  inputRef?: any
  error?: boolean
  alignTop?: boolean
  disabled?: boolean
}

// source: https://codepen.io/melnik909/pen/YjGZqQ

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    marginLeft: 0,
  },
  itemsTop: {
    alignItems: "flex-start",
  },
  checkbox: {
    padding: 0,
    zIndex: 1,
  },
  label: {
    marginLeft: theme.spacing(1),
  },
  toggle: {
    display: "inline-flex",
    position: "relative",
  },
  toggleInput: {
    width: ({ size }: any) => size,
    height: ({ size }: any) => size,
    opacity: 0,
    position: "absolute",
    top: 0,
    left: 0,
    margin: 0,

    "&:not(:disabled)": {
      cursor: "pointer",
    },
  },
  toggleLabel: {
    display: "inline-flex",
    minHeight: ({ size }: any) => size,
    paddingLeft: ({ size, toggleIndent }: any) =>
      `calc(${size} + ${toggleIndent})`,

    "&::after": {
      content: "''",
      boxSizing: "border-box",
      width: "1em",
      height: "1em",
      fontSize: ({ size }: any) => size,
      // backgroundColor: "transparent",
      backgroundColor: theme.palette.action.disabled,
      border: ({ borderWidth, borderColor }: any) =>
        `${borderWidth} solid ${borderColor}`,
      position: "absolute",
      left: 0,
      top: 0,
      willChange: "background-color",
      transition: "background-color .2s ease-out, border-color .2s ease-out",
      borderRadius: "2px",
      zIndex: -1,
    },

    "&::before": {
      content: "''",
      width: 0,
      height: 0,
      fontSize: ({ size }: any) => size,

      borderLeftWidth: 0,
      borderBottomWidth: 0,
      borderLeftStyle: "solid",
      borderBottomStyle: "solid",
      borderColor: theme.palette.common.white,
      position: "absolute",
      top: "50%",
      left: "19%",
      // zIndex: 3,

      transformOrigin: "left top",
      transform: "rotate(-40deg) skew(10deg)",
    },
  },
  toggleLabelChecked: {
    "&::after": {
      backgroundColor: ({ color }: any) => color,
      borderColor: ({ color }: any) => color,
    },
    "&::before": {
      //   transition:
      //     "opacity .1s ease-out .25s, width .1s ease-out .5s, height .2s ease-out .3s",
      opacity: 1,

      width: ".5em",
      height: ".3em",
      borderLeftWidth: "2px",
      borderBottomWidth: "2px",
    },
  },
}))

const Icon = ({ classes, checked }: any) => (
  <label className={classes.toggle}>
    <input
      className={classes.toggleInput}
      type="checkbox"
      defaultChecked={checked}
    />
    <span
      className={classNames(classes.toggleLabel, {
        [classes.toggleLabelChecked]: checked,
      })}
    />
  </label>
)

const CustomCheckbox = ({
  className,
  customLabelClass,
  onChange,
  onValueChange,
  children,
  value: checked,
  defaultChecked,
  alignTop,
  disabled,
  ...other
}: Props) => {
  const classes = useStyles({
    size: "1rem",
    borderWidth: "1.75px",
    color: lightBlack,
    borderColor: lightestGray,
    toggleIndent: ".1rem",
  })

  const handleChange = (newValue: boolean) => {
    onChange?.(newValue)
    onValueChange?.(newValue)
  }

  return (
    <FormControlLabel
      classes={{
        root: classNames(
          classes.root,
          {
            [classes.itemsTop]: alignTop,
          },
          className
        ),
        label: classNames(classes.label, customLabelClass),
      }}
      control={
        <Checkbox
          disableRipple
          icon={<Icon classes={classes} checked={false} />}
          checkedIcon={<Icon classes={classes} checked={true} />}
          className={classes.checkbox}
          defaultChecked={defaultChecked}
        />
      }
      label={children}
      checked={checked}
      onChange={(event, checked) => handleChange(checked)}
      disabled={disabled}
      {...other}
    />
  )
}

CustomCheckbox.defaultProps = {
  alignTop: true,
}

export default React.forwardRef((props: any, ref) => (
  <CustomCheckbox {...props} inputRef={ref} />
))
